import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import { getCompanySpeedToLead } from 'apis/RecommendationsAPI';
import { StyledTable } from 'common/Table/StyledTable';
import DetailsCell from 'components/AiCompetitiveIntelligence/AICICompanyCommunityCells/DetailsCell';
import PositionCell from 'components/AiCompetitiveIntelligence/AICICompanyCommunityCells/PositionCell';
import ScoreCell from 'components/AiCompetitiveIntelligence/AICICompanyCommunityCells/ScoreCell';
import AiCiTableWrapper from 'components/AiCompetitiveIntelligence/AiCiTableWrapper';
import useRecommendationsContext from 'components/AiCompetitiveIntelligence/RecommendationsContext';
import RecommendationsNotFound from 'components/AiCompetitiveIntelligence/RecommendationsNotFound';
import ErrorMessage from 'components/lib/ErrorMessage';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import PillSwitcher from 'components/lib/PillSwitcher';
import { CompanySpeedToLeadRow } from 'types/recommendations';

type ScoreView = 'overall' | 'speed' | 'quality';

const SCORE_TABS = [
  { key: 'overall', label: 'Overall' },
  { key: 'speed', label: 'Speed' },
  { key: 'quality', label: 'Quality' },
];

const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 10px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 180px;
`;

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 850px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border: 1px solid var(--line-gray);
  border-radius: 12px;
  background: var(--white);
`;

const MetricLabel = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const MetricValue = styled.span`
  font-size: 28px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CommunityName = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CommunityPosition = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const Findings = styled.div`
  display: -webkit-box;
  overflow: hidden;
  color: var(--text-secondary);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const TableWrapper = styled(AiCiTableWrapper)`
  margin-top: 0;
`;

const scoreField = (view: ScoreView): keyof CompanySpeedToLeadRow => `${view}_score`;

const CompanySpeedToLead = () => {
  const navigate = useNavigate();
  const { company } = useRecommendationsContext();
  const [scoreView, setScoreView] = useState<ScoreView>('overall');
  const { data, isError, isLoading } = useQuery({
    queryKey: ['ai-competitive-intelligence-company-speed-to-lead', company?.id],
    queryFn: () => getCompanySpeedToLead(company?.id ?? -1),
    enabled: Boolean(company?.id),
    refetchOnWindowFocus: false,
  });
  const field = scoreField(scoreView);
  const rows = useMemo(
    () => [...(data?.rows ?? [])].sort((left, right) => Number(right[field]) - Number(left[field])),
    [data?.rows, field]
  );
  const averageScore = data?.averages[scoreView] ?? null;
  const columns: ColumnsType<CompanySpeedToLeadRow> = [
    {
      title: 'PORTFOLIO RANK',
      key: 'community_name',
      render: (_value, row) => (
        <div>
          <CommunityName>{row.community_name}</CommunityName>
          <CommunityPosition>
            #{rows.findIndex((item) => item.community_id === row.community_id) + 1} in portfolio
          </CommunityPosition>
        </div>
      ),
    },
    {
      title: `${scoreView.toUpperCase()} SCORE`,
      key: field,
      width: 160,
      render: (_value, row) => <ScoreCell score={Number(row[field])} averageScore={averageScore} />,
    },
    {
      title: 'OVERALL POSITION VS COMP.',
      dataIndex: 'rank',
      key: 'rank',
      width: 170,
      render: (rank, row) => (
        <PositionCell rank={rank} performance={row.performance} competitorsCount={row.competitors_count} />
      ),
    },
    {
      title: 'FINDINGS',
      dataIndex: 'summary',
      key: 'summary',
      render: (summary) => <Findings title={summary}>{summary || '-'}</Findings>,
    },
    {
      title: '',
      key: 'details',
      width: 130,
      render: (_value, row) => (
        <DetailsCell
          onClick={() => navigate(`/ai-competitive-intelligence/speed-to-lead?communityId=${row.community_id}`)}
        />
      ),
    },
  ];

  return (
    <>
      {isLoading && (
        <LoaderContainer>
          <LoaderRibbed active />
        </LoaderContainer>
      )}
      <ErrorMessage text="Failed to load company Speed to Lead results. Please try again." active={isError} />
      {!isLoading && !isError && !data?.analyzed_communities && (
        <RecommendationsNotFound
          title="There are no company Speed to Lead insights yet."
          description="Ask an administrator to generate Speed to Lead analyses for the company's communities."
        />
      )}
      {!isLoading && !isError && Boolean(data?.analyzed_communities) && (
        <Main>
          <Metrics>
            <Metric>
              <MetricLabel>Communities analyzed</MetricLabel>
              <MetricValue>
                {data?.analyzed_communities}/{data?.total_communities}
              </MetricValue>
            </Metric>
            <Metric>
              <MetricLabel>Average overall score</MetricLabel>
              <MetricValue>{data?.averages.overall ?? '-'}</MetricValue>
            </Metric>
            <Metric>
              <MetricLabel>Average speed score</MetricLabel>
              <MetricValue>{data?.averages.speed ?? '-'}</MetricValue>
            </Metric>
            <Metric>
              <MetricLabel>Average quality score</MetricLabel>
              <MetricValue>{data?.averages.quality ?? '-'}</MetricValue>
            </Metric>
          </Metrics>
          <PillSwitcher items={SCORE_TABS} activeKey={scoreView} onChange={(key) => setScoreView(key as ScoreView)} />
          <TableWrapper>
            <StyledTable
              columns={columns as any}
              dataSource={rows}
              rowKey="community_id"
              pagination={{
                size: 'small',
                pageSize: 10,
                position: ['bottomLeft'],
                showSizeChanger: false,
                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} records`,
              }}
              tableLayout="fixed"
            />
          </TableWrapper>
        </Main>
      )}
    </>
  );
};

export default CompanySpeedToLead;
